/**
 * Writing Sample Modal — the optional "voice fingerprint" exercise.
 *
 * The user writes ~1,000 characters as if messaging a coworker. The raw text
 * lives ONLY in this component's state (never localStorage) and leaves the
 * browser once, in the analyze request body. The server stores only an abstract
 * trait fingerprint — never the words. See features/user_writing_style.md §4.2.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, X, Check } from 'lucide-react';
import { api } from '../services/api';
import type { CalibrateRoundResult, WritingFingerprint } from '../types';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';

// The four scalar traits the fine-tune flow calibrates, in round order. Mirrors
// CALIBRATABLE_TRAITS on the server (features/user_writing_style.md §11.4).
const CALIBRATE_TRAITS = ['cadence', 'warmth', 'directness', 'concreteness'] as const;
const TRAIT_LABEL: Record<string, string> = {
  cadence: 'Cadence',
  warmth: 'Warmth',
  directness: 'Directness',
  concreteness: 'Concreteness',
};

type Adjustment = { trait: string; kept: 'kept' | 'adjusted' | 'skipped' | 'neither'; value: string | null };

// Human labels for the fingerprint readout shown when the modal is reopened
// with a fingerprint already on file. Order = display order.
const READOUT_ORDER: Array<[key: string, label: string]> = [
  ['cadence', 'Cadence'],
  ['warmth', 'Warmth'],
  ['directness', 'Directness'],
  ['concreteness', 'Concreteness'],
  ['sentence_length', 'Sentence length'],
  ['sentence_length_variance', 'Sentence variety'],
  ['word_length', 'Word length'],
  ['contractions', 'Contractions'],
  ['em_dashes', 'Em-dashes'],
  ['semicolons', 'Semicolons'],
  ['parentheticals', 'Parentheticals'],
  ['exclamations', 'Exclamation marks'],
  ['connectives', 'Favorite connectives'],
  ['openers', 'Sentence openers'],
];

function readoutLines(traits: Record<string, string | string[]> | undefined): string[] {
  if (!traits) return [];
  const lines: string[] = [];
  for (const [key, label] of READOUT_ORDER) {
    const v = traits[key];
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      if (v.length) lines.push(`${label}: ${v.map((s) => s.replace(/_/g, ' ')).join(', ')}`);
    } else {
      lines.push(`${label}: ${String(v).replace(/_/g, ' ')}`);
    }
  }
  return lines;
}

interface WritingSampleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void; // parent refreshes preferences so the section chip flips
}

interface Thresholds {
  min_chars: number;
  sweet_min: number;
  sweet_max: number;
  max_chars: number;
}

// Suggestion starters only — which one (if any) the user picks has ZERO effect
// on the analysis, so there is deliberately no selection state.
const PROMPTS: string[] = [
  'Describe a time in your life that was impactful to the type of person you’ve become.',
  'Tell a coworker about a project you’re proud of — what happened, and why it mattered to you.',
  'What’s something you’ve changed your mind about in the last few years? What changed it?',
];

export function WritingSampleModal({ isOpen, onClose, onSuccess }: WritingSampleModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [thresholds, setThresholds] = useState<Thresholds | null>(null);
  const [thresholdError, setThresholdError] = useState(false);
  const [text, setText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [directiveLines, setDirectiveLines] = useState<string[] | null>(null);

  // Existing fingerprint (fetched on open) — when present, the intro copy is
  // replaced by a readout of the current fingerprint + a regenerate note.
  const [existingFp, setExistingFp] = useState<WritingFingerprint | null>(null);

  // Calibration ("fine-tune") flow state.
  const [calibrating, setCalibrating] = useState(false);
  const [calibrateDone, setCalibrateDone] = useState(false);
  // "Neither sounded like me" state (per round).
  const [neitherOpen, setNeitherOpen] = useState(false);
  const [neitherText, setNeitherText] = useState('');
  const [sendingNeither, setSendingNeither] = useState(false);
  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState<CalibrateRoundResult | null>(null);
  const [roundLoading, setRoundLoading] = useState(false);
  const [picking, setPicking] = useState(false);
  const [roundError, setRoundError] = useState('');
  const [currentIsA, setCurrentIsA] = useState(true);
  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);

  useEscapeClose(onClose, isOpen);
  useFocusTrap(modalRef, isOpen);

  // Fetch admin-tunable thresholds on open. No hardcoded fallback — the Analyze
  // button stays disabled until this resolves.
  useEffect(() => {
    if (!isOpen) return;
    setThresholds(null);
    setThresholdError(false);
    let cancelled = false;
    api.getPublicSetting('writing_sample_thresholds')
      .then((res) => {
        if (cancelled) return;
        const v = res.setting_value as Thresholds;
        if (v && typeof v.min_chars === 'number') setThresholds(v);
        else setThresholdError(true);
      })
      .catch(() => { if (!cancelled) setThresholdError(true); });
    // Existing fingerprint (if any) — drives the "current fingerprint" readout
    // in place of the intro copy. Best-effort: a fetch failure just shows the
    // standard intro.
    setExistingFp(null);
    api.getPreferences()
      .then((prefs) => { if (!cancelled) setExistingFp(prefs.writing_fingerprint ?? null); })
      .catch(() => { /* intro copy is the fallback */ });
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const resetAndClose = () => {
    // Discard the raw text — it never persists anywhere.
    setText('');
    setError('');
    setDirectiveLines(null);
    setAnalyzing(false);
    setCalibrating(false);
    setCalibrateDone(false);
    setRound(null);
    setRoundError('');
    setRoundLoading(false);
    setPicking(false);
    setAdjustments([]);
    setRoundIndex(0);
    setNeitherOpen(false);
    setNeitherText('');
    setSendingNeither(false);
    onClose();
  };

  // --- Calibration ("fine-tune") — features/user_writing_style.md §11.4 ------
  const loadRound = async (idx: number) => {
    const trait = CALIBRATE_TRAITS[idx];
    setRound(null);
    setRoundError('');
    setNeitherOpen(false);
    setNeitherText('');
    setRoundLoading(true);
    try {
      const res = await api.calibrateRound(trait);
      setCurrentIsA(Math.random() < 0.5); // randomize A/B display order
      setRound(res);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not prepare this round — you can skip it.';
      setRoundError(msg);
    } finally {
      setRoundLoading(false);
    }
  };

  const startCalibration = () => {
    setCalibrating(true);
    setCalibrateDone(false);
    setAdjustments([]);
    setRoundIndex(0);
    void loadRound(0);
  };

  const advance = (record: Adjustment) => {
    setAdjustments((prev) => [...prev, record]);
    const next = roundIndex + 1;
    if (next >= CALIBRATE_TRAITS.length) {
      setCalibrateDone(true);
      onSuccess(); // fingerprint changed server-side; let the parent re-read
    } else {
      setRoundIndex(next);
      void loadRound(next);
    }
  };

  const pick = async (side: 'current' | 'flipped') => {
    if (!round || picking) return;
    const trait = round.trait;
    const winnerValue = side === 'current' ? round.current_value ?? 'absent' : round.flipped_value;
    setPicking(true);
    setRoundError('');
    try {
      await api.calibratePick(trait, winnerValue);
      setPicking(false);
      advance({
        trait,
        kept: side === 'current' ? 'kept' : 'adjusted',
        value: side === 'current' ? round.current_value : round.flipped_value,
      });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not save that choice — you can skip this round.';
      setRoundError(msg);
      setPicking(false);
    }
  };

  const skipRound = () => {
    setNeitherOpen(false);
    setNeitherText('');
    advance({ trait: CALIBRATE_TRAITS[roundIndex], kept: 'skipped', value: null });
  };

  // "Neither sounded like me" — same trait outcome as skip (no change), but it
  // gives the user closure and optionally captures WHY as app feedback. The
  // feedback text is the user's own commentary, never their writing sample.
  const submitNeither = async () => {
    const trait = round?.trait || CALIBRATE_TRAITS[roundIndex];
    const note = neitherText.trim();
    setSendingNeither(true);
    if (note) {
      try {
        await api.submitAppFeedback({
          area: 'general',
          sentiment: 'rough',
          message: `[Writing-style fine-tune — "${TRAIT_LABEL[trait] || trait}" round, neither version matched] ${note}`,
          allow_followup: false,
        });
      } catch {
        // Best-effort — never block the flow on feedback delivery.
      }
    }
    setSendingNeither(false);
    setNeitherOpen(false);
    setNeitherText('');
    advance({ trait, kept: 'neither', value: null });
  };

  const len = text.length;
  const thresholdsReady = thresholds !== null;
  const canAnalyze = thresholdsReady && len >= (thresholds?.min_chars ?? Infinity) && !analyzing;

  // Counter color + copy driven by the thresholds.
  let counterClass = 'text-gray-400';
  let counterMsg = '';
  if (thresholds) {
    const { min_chars, sweet_min, sweet_max, max_chars } = thresholds;
    if (len < min_chars) {
      counterClass = 'text-red-600';
      counterMsg = `~${Math.max(0, min_chars - len)} more to go`;
    } else if (len < sweet_min) {
      counterClass = 'text-green-600';
      counterMsg = 'Got enough — keep going if you like';
    } else if (len <= sweet_max) {
      counterClass = 'text-green-600';
      counterMsg = 'Perfect length — stop whenever you’re ready';
    } else if (len < max_chars - 500) {
      counterClass = 'text-green-600';
      counterMsg = 'That’s plenty — more won’t hurt';
    } else {
      counterClass = 'text-amber-600';
      counterMsg = 'OK, that’s really enough 🙂';
    }
  }

  const handleAnalyze = async () => {
    if (!canAnalyze) return;
    setAnalyzing(true);
    setError('');
    try {
      const res = await api.analyzeWritingSample(text);
      const lines = (res.directive_preview || '')
        .split('\n')
        .filter((l) => l.trim().startsWith('-'))
        .map((l) => l.replace(/^\s*-\s*/, '').trim());
      setDirectiveLines(lines.slice(0, 6));
      onSuccess();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not analyze the writing sample — please try again.';
      setError(msg);
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Writing Sample"
    >
      {/* Width matches the Edit Resume lightbox (max-w-[986px]) */}
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-[986px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            Generate your Writing Style &ldquo;Fingerprint&rdquo;
          </h2>
          <button
            onClick={resetAndClose}
            className="p-1 hover:bg-gray-100 rounded"
            aria-label="Close"
            data-help-target="writingsample.modal.close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {calibrateDone ? (
          /* Finish pane — summary of adjustments. */
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-green-700">
              <Check className="w-5 h-5" />
              <p className="font-semibold">Fine-tuning complete</p>
            </div>
            <p className="text-sm text-gray-600">Here&apos;s what changed:</p>
            <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
              {adjustments.map((a, i) => (
                <li key={i}>
                  {TRAIT_LABEL[a.trait] || a.trait}:{' '}
                  {a.kept === 'kept'
                    ? 'kept'
                    : a.kept === 'adjusted'
                      ? `adjusted to ${a.value}`
                      : a.kept === 'neither'
                        ? 'no change (neither version matched)'
                        : 'skipped'}
                </li>
              ))}
            </ul>
            <div className="flex justify-end pt-2 border-t">
              <button
                onClick={resetAndClose}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Done
              </button>
            </div>
          </div>
        ) : calibrating ? (
          /* Calibration pane — one A/B round at a time. */
          <div className="p-6 space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Which sounds more like you?
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Same paragraph, two styles. Pick the one closer to your voice — or skip.
              </p>
            </div>

            {/* Progress dots */}
            <div className="flex items-center gap-1.5">
              {CALIBRATE_TRAITS.map((t, i) => (
                <span
                  key={t}
                  className={`h-2 w-2 rounded-full ${
                    i < roundIndex
                      ? 'bg-primary-600'
                      : i === roundIndex
                        ? 'bg-primary-400'
                        : 'bg-gray-200'
                  }`}
                />
              ))}
              <span className="text-xs text-gray-400 ml-2">
                Round {Math.min(roundIndex + 1, CALIBRATE_TRAITS.length)} of {CALIBRATE_TRAITS.length}
              </span>
            </div>

            {roundLoading ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm py-10 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" />
                Writing two versions… ~10s
              </div>
            ) : round ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(currentIsA
                  ? ([
                      { label: 'A', text: round.text_current, side: 'current' as const },
                      { label: 'B', text: round.text_flipped, side: 'flipped' as const },
                    ])
                  : ([
                      { label: 'A', text: round.text_flipped, side: 'flipped' as const },
                      { label: 'B', text: round.text_current, side: 'current' as const },
                    ])
                ).map((col) => (
                  <div key={col.label} className="flex flex-col border border-gray-200 rounded-lg p-3">
                    <span className="text-xs font-semibold text-gray-400 mb-2">Version {col.label}</span>
                    <p className="text-sm text-gray-700 flex-1 whitespace-pre-wrap">{col.text}</p>
                    <button
                      onClick={() => pick(col.side)}
                      disabled={picking}
                      data-help-target="writingsample.modal.calibrate.pick"
                      className="mt-3 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
                    >
                      This sounds more like me
                    </button>
                  </div>
                ))}
              </div>
            ) : null}

            {/* "Neither" — no winner, optional feedback, same trait outcome as skip. */}
            {round && !roundLoading && (
              <div className="space-y-2">
                {!neitherOpen ? (
                  <div className="flex justify-center">
                    <button
                      onClick={() => setNeitherOpen(true)}
                      disabled={picking || sendingNeither}
                      className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
                    >
                      Neither sounded like me
                    </button>
                  </div>
                ) : (
                  <div className="border border-gray-200 bg-gray-50 rounded-lg p-3 space-y-2">
                    <p className="text-sm text-gray-700">
                      No problem — we&apos;ll leave this trait as-is. What felt off? (optional)
                    </p>
                    <textarea
                      value={neitherText}
                      onChange={(e) => setNeitherText(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 resize-y text-sm"
                      placeholder="Both felt too formal / too stiff / not my rhythm…"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => { setNeitherOpen(false); setNeitherText(''); }}
                        disabled={sendingNeither}
                        className="px-3 py-1.5 text-gray-500 rounded-lg hover:bg-gray-100 text-sm disabled:opacity-50"
                      >
                        Back
                      </button>
                      <button
                        onClick={() => void submitNeither()}
                        disabled={sendingNeither}
                        className="flex items-center gap-2 px-4 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm disabled:opacity-50"
                      >
                        {sendingNeither && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        {neitherText.trim() ? 'Send & continue' : 'Continue'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {roundError && <p className="text-sm text-red-600">{roundError}</p>}

            <div className="flex justify-between items-center pt-2 border-t">
              <button
                onClick={skipRound}
                disabled={picking || sendingNeither}
                className="text-sm text-gray-500 hover:text-gray-700 underline disabled:opacity-50"
              >
                Skip this round
              </button>
              <button
                onClick={resetAndClose}
                className="px-6 py-2 text-gray-600 rounded-lg hover:bg-gray-100"
              >
                Done
              </button>
            </div>
          </div>
        ) : directiveLines ? (
          /* Confirmation pane — traits only; the sample was discarded. */
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-green-700">
              <Check className="w-5 h-5" />
              <p className="font-semibold">Here&apos;s what we noticed</p>
            </div>
            <p className="text-sm text-gray-600">
              Traits only — your words were discarded. We&apos;ll apply these to your
              AI-generated Summary, Cover Letter, Highlights, and Elevator Pitch.
            </p>
            <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
              {directiveLines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>

            {/* Optional fine-tune: a short A/B over the four register traits. */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-sm font-semibold text-gray-800">
                Fine-tune your fingerprint <span className="font-normal text-gray-400">(optional)</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Four quick rounds — we show you two versions of the same paragraph and you
                pick the one that sounds more like you. Takes about a minute.
              </p>
            </div>

            <div className="flex justify-between items-center pt-2 border-t">
              <button
                onClick={startCalibration}
                data-help-target="writingsample.modal.calibrate"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
              >
                Start fine-tuning <span className="font-normal opacity-80">(optional)</span>
              </button>
              <button
                onClick={resetAndClose}
                className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {existingFp?.traits && readoutLines(existingFp.traits).length > 0 ? (
              /* Reopened with a fingerprint on file: show the current readout
                 instead of the intro, plus the regenerate note. */
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-700">
                  <Check className="w-5 h-5" />
                  <p className="font-semibold">
                    Your current fingerprint
                    {existingFp.created_at
                      ? ` (captured ${new Date(existingFp.created_at).toLocaleDateString()})`
                      : ''}
                  </p>
                </div>
                <ul className="text-sm text-gray-700 columns-1 sm:columns-2 gap-8 list-disc pl-5 space-y-1">
                  {readoutLines(existingFp.traits).map((line, i) => (
                    <li key={i} className="break-inside-avoid">{line}</li>
                  ))}
                </ul>
                <p className="text-sm text-gray-600">
                  To regenerate and replace your current fingerprint, type up a new writing
                  sample below.
                </p>
              </div>
            ) : (
            <div className="text-sm text-gray-700 space-y-3">
              <p>
                AI resumes often come with that &ldquo;AI Smell&rdquo; that recruiters and
                hiring managers detect a mile away. This is why Skillsnap customizes your
                words, rather than write for you. That said, you do have the option to have
                AI re-write certain sections per job; completing this writing exercise can
                make those outputs more genuine to your style.
              </p>
              <p>
                <strong>How does this work?</strong>
                <br />
                You hand-write a paragraph or two. Skillsnap turns this into a
                &ldquo;fingerprint&rdquo; of your writing style: measuring your punctuation
                habits, sentence length &amp; rhythm, average word length, contraction use,
                connective words, and overall warmth &amp; directness. When AI re-writes
                sections on your behalf, it matches those characteristics in new content,
                making it more authentic to your particular voice.
              </p>
              <p>
                <strong>What should I write?</strong>
                <br />
                Doesn&apos;t matter; it&apos;s not about the content, but rather the style,
                cadence, and feel of your voice. We save the fingerprint, NOT your text (so
                say anything you like!). We provide a few prompts, so you&apos;re not
                starting at a blank page, but those are optional. Importantly, write your
                own, natural words, as if you&apos;re messaging a friendly co-worker —
                casually professional. And if you&apos;ve already written Interview Q&amp;A
                answers here in SkillSnap, those quietly strengthen your fingerprint too —
                no extra work needed.
              </p>
            </div>
            )}

            {/* Starter prompts — pure suggestions; picking one (or none) has no
                effect on the fingerprint, so there's nothing to select. */}
            <div data-help-target="writingsample.modal.prompt">
              <ul className="space-y-1.5 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 list-disc pl-8">
                {PROMPTS.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>

            <p className="text-sm font-semibold text-gray-900">Ready to get started?</p>

            <div>
              <textarea
                data-help-target="writingsample.modal.text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 resize-y text-sm"
                placeholder="Just write naturally…"
              />
              <div
                className={`flex items-center justify-between text-xs mt-1 ${counterClass}`}
                data-help-target="writingsample.modal.counter"
              >
                <span>{thresholdsReady ? counterMsg : 'Loading…'}</span>
                <span>{len} characters</span>
              </div>
            </div>

            {thresholdError && (
              <p className="text-sm text-red-600">
                Couldn&apos;t load the writing-sample settings. Please close and try again.
              </p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <button
                onClick={resetAndClose}
                className="px-4 py-2 text-gray-600 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleAnalyze}
                disabled={!canAnalyze}
                data-help-target="writingsample.modal.analyze"
                className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {analyzing && <Loader2 className="w-4 h-4 animate-spin" />}
                {analyzing ? 'Analyzing your voice… (~10 seconds)' : 'Generate My Fingerprint'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
